import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

export default function PrivacyEnPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PageHead
        title="Privacy Policy | NetBasis International"
        description="Information on the protection of your personal data and data collection at NetBasis International in accordance with GDPR."
        ogUrl="https://net-basis.net/en/privacy"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero Image Section */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src="https://net-basis.net/Bilder/netbasis-legal-notice-privacy-policy.webp"
                alt="Privacy Policy"
                title="Privacy Policy"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            {/* H1 */}
            <h1 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              Privacy Policy
            </h1>

            {/* ========== SECTION 1 ========== */}
            <section className="mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
                1. Data Protection at a Glance
              </h2>

              <div className="mb-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  General Information
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  The following notes provide a simple overview of what happens to your personal data when you visit this website. Personal data is any data with which you can be personally identified. For detailed information on the subject of data protection, please refer to our Privacy Policy listed below this text.
                </p>
              </div>

              <div className="space-y-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600">
                  Data Recording on Our Website
                </h3>

                <div>
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                    <strong className="font-semibold text-foreground-950">Who is responsible for data recording on this website?</strong><br />
                    The data processing on this website is carried out by the website operator. You can find their contact details in the &quot;Information on the Responsible Party&quot; section of this privacy policy.
                  </p>
                </div>

                <div>
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                    <strong className="font-semibold text-foreground-950">How do we record your data?</strong><br />
                    On the one hand, your data is collected when you communicate it to us. This can be, for example, data that you enter into a contact form.
                  </p>
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                    Other data is recorded automatically by our IT systems when you visit the website. This is primarily technical data (e.g., internet browser, operating system, or time of the page view). This data is recorded automatically as soon as you enter our website.
                  </p>
                </div>

                <div>
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                    <strong className="font-semibold text-foreground-950">What do we use your data for?</strong><br />
                    Part of the data is collected to ensure the error-free provision of the website. Other data can be used to analyze your user behavior.
                  </p>
                </div>

                <div>
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                    <strong className="font-semibold text-foreground-950">What rights do you have regarding your data?</strong><br />
                    You have the right to receive information about the origin, recipient, and purpose of your stored personal data free of charge at any time. You also have a right to request the rectification or erasure of this data. Under certain circumstances, you also have the right to request the restriction of the processing of your personal data as well as a right to object to the processing. For this purpose, as well as for further questions on the subject of data protection, you can contact us at any time at the address given in the legal notice (Impressum). Furthermore, you have a right of appeal to the competent supervisory authority.
                  </p>
                </div>
              </div>
            </section>

            {/* Divider */}
            <div className="w-full h-px bg-background-300 my-10" />

            {/* ========== SECTION 2 ========== */}
            <section className="mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
                2. Hosting
              </h2>

              <div className="mb-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  External Hosting by STRATO
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Our website is hosted by an external service provider (hoster). The personal data collected on this website is stored on the hoster&apos;s servers. This primarily involves IP addresses, contact requests, meta and communication data, website access, and other data generated via a website.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  The hoster is used for the purpose of fulfilling contracts with our potential and existing customers (Art. 6 Abs. 1 lit. b DSGVO) and in the interest of a secure, fast, and efficient provision of our online offer by a professional provider (Art. 6 Abs. 1 lit. f DSGVO).
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  We use the following hoster:<br />
                  STRATO AG<br />
                  Otto-Ostrowski-Straße 7<br />
                  10249 Berlin<br />
                  Germany
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  <strong className="font-semibold text-foreground-950">Conclusion of a Data Processing Agreement:</strong><br />
                  In order to ensure data protection-compliant processing, a Data Processing Agreement (DPA) is active with STRATO AG (integrated into STRATO&apos;s General Terms and Conditions). This guarantees that the hoster processes the personal data of our website visitors only in accordance with our instructions and in compliance with the GDPR.
                </p>
              </div>

              <div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Server Log Files
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  The provider of the pages automatically collects and stores information in so-called server log files, which your browser automatically transmits to us. These are:
                </p>
                <ul className="mt-3 space-y-1.5 list-disc pl-6 font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  <li>Browser type and browser version</li>
                  <li>Operating system used</li>
                  <li>Referrer URL (the previously visited page)</li>
                  <li>Host name of the accessing computer</li>
                  <li>Time of the server request</li>
                  <li>IP address</li>
                </ul>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  This data is not merged with other data sources. This data is recorded on the basis of Art. 6 Abs. 1 lit. f DSGVO. The website operator has a legitimate interest in the technically error-free presentation and optimization of their website – the server log files must be recorded for this purpose. The data is automatically deleted after <strong className="font-semibold text-foreground-950">7 days</strong>.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Cloudflare CDN (Font Awesome &amp; Remix Icon)
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  We use the Cloudflare Content Delivery Network (CDN) to provide symbols and icons (Font Awesome and Remix Icon). The provider is Cloudflare Inc., 101 Townsend St, San Francisco, CA 94107, USA.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  A CDN is a service that helps deliver content from our website, particularly scripts or media files, more quickly via a network of regionally or internationally distributed servers. When you access these symbols, your browser establishes a direct connection to Cloudflare&apos;s servers. This transmits your IP address and information about which of our pages you have visited to Cloudflare.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  The use of the CDN is based on our legitimate interest in a fast, stable, and secure provision of our website (Art. 6 Abs. 1 lit. f GDPR). Data transmission to the USA is based on the EU Commission&apos;s standard contractual clauses. Details can be found in Cloudflare&apos;s privacy policy: https://cloudflare.com.
                </p>
              </div>
            </section>

            {/* Divider */}
            <div className="w-full h-px bg-background-300 my-10" />

            {/* ========== SECTION 3 ========== */}
            <section>
              <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
                3. General Notes and Mandatory Information
              </h2>

              <div className="mb-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Data Protection
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  The operators of these pages take the protection of your personal data very seriously. We treat your personal data confidentially and in accordance with the statutory data protection regulations and this privacy policy.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  When you use this website, various personal data are collected. No analysis or tracking cookies are used when you access our website. Only a technically necessary cookie is set to store your decision regarding the activation of the Google Maps map service, if you use it.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  We would like to point out that data transmission over the Internet (e.g., when communicating by e-mail) can have security gaps. Complete protection of data against access by third parties is not possible.
                </p>
              </div>

              <div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Information on the Responsible Party
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  The responsible party for data processing on this website is:
                </p>
                <div className="mt-3 p-5 bg-background-50 border border-background-200 rounded-lg">
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                    Christof Günther<br />
                    NetBasis International<br />
                    Heinrichstraße 45<br />
                    40239 Düsseldorf<br />
                    Germany
                  </p>
                  <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                    Phone: +49 (0) 211 / 544 193 80<br />
                    E-mail: contact@net-basis.com
                  </p>
                </div>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-4">
                  The responsible party is the natural or legal person who alone or jointly with others decides on the purposes and means of processing personal data (e.g., names, e-mail addresses, etc.).
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Storage Period
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Unless a more specific storage period has been specified within this privacy policy, your personal data will remain with us until the purpose for data processing no longer applies. If you assert a legitimate request for deletion or revoke your consent to data processing, your data will be deleted, provided we have no other legally permissible reasons for storing your personal data (e.g., tax or commercial law retention periods); in the latter case, the deletion will take place after these reasons no longer apply.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Revocation of Your Consent to Data Processing
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Many data processing operations are only possible with your express consent. You can revoke any consent you have already given at any time. An informal communication by e-mail to us is sufficient for this purpose. The legality of the data processing carried out up to the revocation remains unaffected by the revocation.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Right to Object to Data Collection in Special Cases and to Direct Marketing (Art. 21 GDPR)
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  IF DATA PROCESSING IS BASED ON ART. 6 ABS. 1 LIT. E OR F GDPR, YOU HAVE THE RIGHT TO OBJECT TO THE PROCESSING OF YOUR PERSONAL DATA AT ANY TIME FOR REASONS ARISING FROM YOUR PARTICULAR SITUATION; THIS ALSO APPLIES TO PROFILING BASED ON THESE PROVISIONS. THE RESPECTIVE LEGAL BASIS ON WHICH PROCESSING IS BASED CAN BE FOUND IN THIS PRIVACY POLICY. IF YOU OBJECT, WE WILL NO LONGER PROCESS YOUR AFFECTED PERSONAL DATA UNLESS WE CAN DEMONSTRATE COMPELLING LEGITIMATE GROUNDS FOR THE PROCESSING WHICH OUTWEIGH YOUR INTERESTS, RIGHTS, AND FREEDOMS, OR THE PROCESSING SERVES TO ASSERT, EXERCISE, OR DEFEND LEGAL CLAIMS (OBJECTION PURSUANT TO ART. 21 ABS. 1 GDPR).
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  IF YOUR PERSONAL DATA IS PROCESSED FOR THE PURPOSE OF DIRECT MARKETING, YOU HAVE THE RIGHT TO OBJECT AT ANY TIME TO THE PROCESSING OF PERSONAL DATA CONCERNING YOU FOR THE PURPOSE OF SUCH MARKETING; THIS ALSO APPLIES TO PROFILING TO THE EXTENT THAT IT IS RELATED TO SUCH DIRECT MARKETING. IF YOU OBJECT, YOUR PERSONAL DATA WILL SUBSEQUENTLY NO LONGER BE USED FOR THE PURPOSE OF DIRECT MARKETING (OBJECTION PURSUANT TO ART. 21 ABS. 2 GDPR).
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Right to Lodge a Complaint with the Competent Supervisory Authority
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  In the event of violations of the GDPR, data subjects have a right to lodge a complaint with a supervisory authority, in particular in the Member State of their habitual residence, their place of work, or the place of the alleged violation. The right to lodge a complaint is without prejudice to other administrative or judicial remedies.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Right to Data Portability
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  You have the right to have data that we process automatically on the basis of your consent or in fulfillment of a contract handed over to you or to a third party in a common, machine-readable format. If you request the direct transfer of the data to another responsible party, this will only be done to the extent that it is technically feasible.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  SSL or TLS Encryption
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  For safety reasons and to protect the transmission of confidential content, such as orders or inquiries that you send to us as the site operator, this site uses SSL or TLS encryption. You can recognize an encrypted connection by the fact that the address line of the browser changes from &quot;http://&quot; to &quot;https://&quot; and by the lock symbol in your browser line. If SSL or TLS encryption is activated, the data you transmit to us cannot be read by third parties.
                </p>
              </div>

              <div className="mt-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Information, Rectification, Restriction of Processing, and Erasure
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  Within the framework of the applicable legal provisions, you have the right to free information about your stored personal data, its origin and recipient, and the purpose of the data processing and, if applicable, a right to rectification or erasure of this data at any time.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  You also have the right to request the restriction of the processing of your personal data. This is possible in the following cases:
                </p>
                <ul className="mt-3 space-y-1.5 list-disc pl-6 font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  <li>If you contest the accuracy of your data (for the duration of the verification).</li>
                  <li>In the event of unlawful processing instead of erasure.</li>
                  <li>If the purpose of the processing no longer applies, but you need the data to defend legal claims.</li>
                  <li>After an objection pursuant to Art. 21 Abs. 1 GDPR during the balancing of interests.</li>
                </ul>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  You can contact us at any time for this purpose as well as for further questions on the subject of personal data.
                </p>
              </div>
            </section>

            {/* Divider */}
            <div className="w-full h-px bg-background-300 my-10" />

            {/* ========== SECTION 4 ========== */}
            <section className="mb-10">
              <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
                4. Plugins and Tools
              </h2>

              <div className="mb-6">
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Local Integration of Google Fonts
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  We use Google Fonts and icon libraries (Font Awesome and Remix Icon) on this website for the uniform and visually appealing presentation of texts and icons.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  <strong className="font-semibold text-foreground-950">The Special Feature:</strong> We have installed all fonts and icons <strong className="font-semibold text-foreground-950">locally on our own web server at STRATO</strong>. When you access our website, there is <strong className="font-semibold text-foreground-950">no connection to external servers</strong> of Google, Cloudflare, or other third-party providers. No IP addresses or connection data are passed on. Use is based on our legitimate interest in a secure, fast, and visually uniform website provision (Art. 6 Abs. 1 lit. f GDPR).
                </p>
              </div>

              <div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Google Maps (With Two-Click Solution)
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  We use the Google Maps map service on our contact page to make it easier for you to find us. The provider is Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  <strong className="font-semibold text-foreground-950">Protection of your privacy (Two-Click Solution):</strong><br />
                  To ensure that your data remains protected when you simply open the contact page, Google Maps is deactivated by default. Initially, only a neutral preview image is displayed. Only when you actively click on the map (&quot;Activate content&quot;) do you grant us your consent (Art. 6 Abs. 1 lit. a GDPR). Only from this moment is the map loaded and your IP address transmitted to Google servers. A technically necessary cookie is set in the browser to store your decision. Details on how user data is handled can be found directly in Google&apos;s privacy policy: https://google.com.
                </p>
              </div>
            </section>

            {/* Divider */}
            <div className="w-full h-px bg-background-300 my-10" />

            {/* ========== SECTION 5 ========== */}
            <section>
              <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
                5. Forms and Contact
              </h2>

              <div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-3">
                  Contact Form
                </h3>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed">
                  If you send us inquiries via the contact form, your details from the inquiry form, including the contact details you provided there, will be stored by us for the purpose of processing the inquiry and in the event of follow-up questions. We do not pass on this data without your consent.
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  The processing of this data is based on Art. 6 Abs. 1 lit. b GDPR, provided that your inquiry is related to the fulfillment of a contract or is necessary for the implementation of pre-contractual measures. In all other cases, the processing is based on our legitimate interest in the effective processing of the inquiries addressed to us (Art. 6 Abs. 1 lit. f GDPR).
                </p>
                <p className="font-body text-sm md:text-base text-foreground-950 leading-relaxed mt-3">
                  The data you enter into the contact form will remain with us until you request deletion, revoke your consent to storage, or the purpose for data storage no longer applies (e.g., after your inquiry has been completely processed). Mandatory statutory provisions – in particular statutory retention periods (e.g., for business e-mails relevant to tax law) – remain unaffected.
                </p>
              </div>
            </section>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}